import { processMarkdownText } from '@circuitry-ai/doc-ui';

export type PromptSection = {
  id: string;
  title: string;
  level: number;
  lineIndex: number;
};

export type MarkdownSectionChunk = {
  section: PromptSection | null;
  content: string;
};

export const SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX = 480;

export const systemPromptEditorStyles = `
  .system-prompt-editor-textarea .nextui-textarea-wrapper,
  .system-prompt-editor-textarea textarea {
    min-height: ${SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX}px !important;
    max-height: ${SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX}px !important;
    height: ${SYSTEM_PROMPT_MODAL_CONTENT_HEIGHT_PX}px !important;
    overflow-y: auto !important;
    resize: none !important;
  }
  .system-prompt-preview-panel pre,
  .system-prompt-preview-panel code { white-space: pre-wrap; }
`;

export function parsePromptSections(text: string): PromptSection[] {
  const lines = processMarkdownText(text).split('\n');
  const sections: PromptSection[] = [];
  lines.forEach((line, lineIndex) => {
    const match = /^(#{1,6})\s+(.+)$/.exec(line);
    if (match) {
      sections.push({
        id: `prompt-section-${sections.length}`,
        title: match[2].trim(),
        level: match[1].length,
        lineIndex,
      });
    }
  });
  return sections;
}

export function splitMarkdownBySections(text: string, sections: PromptSection[]): MarkdownSectionChunk[] {
  const lines = processMarkdownText(text).split('\n');
  if (sections.length === 0) {
    return [{ section: null, content: processMarkdownText(text) }];
  }

  const chunks: MarkdownSectionChunk[] = [];
  let buffer: string[] = [];
  let activeSection: PromptSection | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    const section = sections.find((s) => s.lineIndex === i);
    if (section) {
      if (buffer.length > 0) {
        chunks.push({ section: activeSection, content: buffer.join('\n') });
      }
      activeSection = section;
      buffer = [lines[i]];
    } else {
      buffer.push(lines[i]);
    }
  }

  if (buffer.length > 0) {
    chunks.push({ section: activeSection, content: buffer.join('\n') });
  }

  return chunks;
}

export function scrollContainerToElement(container: HTMLElement, target: HTMLElement) {
  requestAnimationFrame(() => {
    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;
    container.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
  });
}

export function resolveNativeTextarea(node: HTMLTextAreaElement | null): HTMLTextAreaElement | null {
  if (!node) return null;
  if (node.tagName === 'TEXTAREA') return node;
  return node.querySelector('textarea');
}

export function getTextareaScrollElement(textarea: HTMLTextAreaElement): HTMLElement {
  const wrapper = textarea.closest('.nextui-textarea-wrapper');
  return wrapper instanceof HTMLElement ? wrapper : textarea;
}

export function scrollTextareaToLine(textarea: HTMLTextAreaElement, lineIndex: number) {
  const lines = textarea.value.split('\n');
  const pos = lines.slice(0, lineIndex).join('\n').length + (lineIndex > 0 ? 1 : 0);
  const scrollEl = getTextareaScrollElement(textarea);

  textarea.focus();
  textarea.setSelectionRange(pos, pos);

  const style = globalThis.getComputedStyle(textarea);
  const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.5 || 20;
  const paddingTop = Number.parseFloat(style.paddingTop) || 0;
  scrollEl.scrollTop = Math.max(0, lineIndex * lineHeight + paddingTop - 24);

  if (scrollEl !== textarea && textarea.scrollTop > 0) {
    scrollEl.scrollTop = textarea.scrollTop;
  }
}
